import './styles.css';
import { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import Logger from './Components/utils/Logger';
import { LoggerType, Dex } from './Components/utils/types';
import { Navbar } from './Components/Navbar';
import { Home } from './Components/pages/Home';
import { TokenManagerCsv } from './Components/pages/TokenManagerCsv';
import { SolManagerCsv } from './Components/pages/SolManagerCsv';
import { CreateTokenForm } from './Components/pages/CreateTokenForm';
import { LoadWallet } from './Components/utils/loadWallet';
import { Footer } from './Components/utils/Footer';
import { RemoveLiquidity } from './Components/pages/Raydium/RemoveLiquidity';
import { DeployPool } from './Components/pages/Raydium/DeployPool';
import { CreateMarket } from './Components/pages/Raydium/CreateMarket';
import { TokenMixerSales } from './Components/pages/TokenMixerSales';
import { Connection, Keypair } from '@solana/web3.js';
import { ManualSwap } from './Components/pages/Raydium/ManualSwap';
import { Freezer } from './Components/pages/Freezer';
import { VolumeWalletControl } from './Components/pages/Raydium/VolumeWalletControl';
import { RapidLaunch } from './Components/pages/Raydium/RapidLaunch';
import getDateString from './Components/utils/get-date-string';
import { useUrlParams } from './Components/hooks/useUrlParams';
import { useBackend } from './Components/hooks/useBackend';
import { useSplToken } from './Components/hooks/useSplToken';
import { removeKeypairDuplicates } from './Components/utils/remove-keypair-duplicates';
import { VolumeMakerV2 } from './Components/pages/Raydium/VolumeMakerV2';
import { LiquidityPoolKeysV4 } from '@raydium-io/raydium-sdk';
import { LiquidityV3 } from './Components/pages/Raydium/LiquidityV3';
import { LiquidityV3ScannerInterval } from './ConfigConstants';
import { VolumeMakerPumpswap } from './Components/pages/Pumpswap/VolumeMakerPumpswap';
import { VolumeSellerPumpswap } from './Components/pages/Pumpswap/VolumeSellerPumpswap';
import { RemoveLiquidityPumpswap } from './Components/pages/Pumpswap/RemoveLiquidityPumpswap';
import { DeployPoolPumpswap } from './Components/pages/Pumpswap/DeployPoolPumpswap';
import { RapidLaunchPumpswap } from './Components/pages/Pumpswap/RapidLaunchPumpswap';

const availableDex: Dex[] = ['pumpswap', 'raydium'];

function App() {
    const { setCurrentMint, getCurrentMint, fetchPoolKeys, setTokenPoolKeys } = useBackend();
    const { bgColor, node, csv, role, sessionId, autoRefresh, autoAction } = useUrlParams();
    const { getNameAndSymbol } = useSplToken();

    const [deployedMarketId, setDeployedMarketId] = useState<string>();
    const [poolKeys, setPoolKeys] = useState<LiquidityPoolKeysV4>();

    const [messages, setMessages] = useState<LoggerType[]>([]);

    const [wallet, setWallet] = useState<Keypair>(new Keypair());
    const [walletLoaded, setWalletLoaded] = useState<boolean>(false);
    const [funder, setFunder] = useState<Keypair>(new Keypair());
    const [bundleBuyKeypairs, setBundleBuyKeypairs] = useState<Keypair[]>([]);
    const [bundleBuyValues, setBundleBuyValues] = useState<number[]>([]);
    const [holderKeypairs, setHolderKeypairs] = useState<Keypair[]>([]);
    const [mixKeypairs, setMixKeypairs] = useState<Keypair[]>([]);
    const [volumeBuyWallets, setVolumeBuyWallets] = useState<Keypair[]>([]);
    const [volumeBuyValues, setVolumeBuyValues] = useState<number[]>([]);
    const [allUniqueWallets, setAllUniqueWallets] = useState<Keypair[]>([]);
    const [allWallets, setAllWallets] = useState<Keypair[]>([]);
    const [freezers, setFreezers] = useState<Keypair[]>([]);
    const [volFunder, setVolFunder] = useState<Keypair | undefined>(undefined);
    const [allWalletRoles, setAllWalletRoles] = useState<string[]>([]);

    const [deployedToken, setDeployedToken] = useState<string>('');
    const [lastUpdatedTokenTime, setLastUpdatedTokenTime] = useState<string>('');

    const [deployedTokenName, setDeployedTokenName] = useState<string>('');
    const [deployedTokenSymbol, setDeployedTokenSymbol] = useState<string>('');

    const [poolBalance, setPoolBalance] = useState<string>('0');
    const [totalWalletsBalance, setTotalWalletsBalance] = useState<number>(0);

    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [parsedCsvData, setParsedCsvData] = useState<any[][] | null>(null);

    const [initialTotalSolBalance, setInitialTotalSolBalance] = useState<number>(0);

    const [isSidebarExpanded, setSidebarExpanded] = useState(true);

    const [currentSlot, setCurrentSlot] = useState<number>(0);

    const logMessage = (message: string, type?: 'success' | 'error' | 'warn') => {
        message = `${message}  ${getDateString()}`;
        setMessages((prevMessages) => [...prevMessages, { message, type }]);
    };

    const httpUrls = process.env.REACT_APP_SOLANA_HTTP_URLS!.split(',');
    const wsUrls = process.env.REACT_APP_SOLANA_WS_URLS!.split(',');
    const domains = httpUrls.map((url) => url.replace(/^https?:\/\//, ''));
    const rpcOptions = domains.reduce(
        (options, domain, index) => {
            options[domain] = new Connection(httpUrls[index], { wsEndpoint: wsUrls[index], commitment: 'processed' });
            return options;
        },
        {} as { [key: string]: Connection },
    );

    const [mainConnectionKey, setMainConnectionKey] = useState<keyof typeof rpcOptions>(domains[0]);
    const [mainConnection, setMainConnection] = useState<Connection>(rpcOptions[mainConnectionKey]);

    const mainRpcChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setMainConnectionKey(event.target.value as keyof typeof rpcOptions);
        setMainConnection(rpcOptions[event.target.value as keyof typeof rpcOptions]);
    };

    const [dex, setDex] = useState<Dex>(availableDex[0]);

    useEffect(() => {
        if (bgColor) {
            const gradients = getComputedStyle(document.body);
            const gradientMap: { [key: string]: string } = {
                grey: gradients.getPropertyValue('--grey-background-gradient'),
                green: gradients.getPropertyValue('--green-background-gradient'),
                blue: gradients.getPropertyValue('--blue-background-gradient'),
                red: gradients.getPropertyValue('--red-background-gradient'),
                pine: gradients.getPropertyValue('--pine-background-gradient'),
                pink: gradients.getPropertyValue('--pink-background-gradient'),
                black: gradients.getPropertyValue('--black-background-gradient'),
            };

            const gradient = gradientMap[bgColor.toLowerCase()] || bgColor;
            document.body.style.background = gradient;
        }

        if (node) {
            try {
                console.log('node', node);
                const id = Number(node);
                setMainConnectionKey(domains[id - 1]);
                setMainConnection(rpcOptions[domains[id - 1]]);
            } catch {
                logMessage('Invalid node id', 'error');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bgColor, node]);

    useEffect(() => {
        try {
            if (deployedToken && role.toUpperCase() === 'MASTER') {
                console.log('Setting current mint');
                setCurrentMint(Number(sessionId), deployedToken);
            }
        } catch {
            console.log('Error setting current mint');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deployedToken, role]);

    const injectToken = async (address: string) => {
        setDeployedToken(address);
        try {
            const [name, symbol] = (await getNameAndSymbol(mainConnection, address, logMessage))!;
            setDeployedTokenName(name);
            setDeployedTokenSymbol(symbol);
        } catch {
            console.log('Error fetching token name and symbol');
        }
        try {
            console.log('Fetching pool keys');
            const poolKeys = await fetchPoolKeys(address);
            setPoolKeys(poolKeys);
        } catch {
            console.log('Error fetching pool keys');
        }
    };

    useEffect(() => {
        const fetchSlot = async () => {
            try {
                const slot = (await mainConnection.getEpochInfo('processed')).absoluteSlot;
                setCurrentSlot(slot - 2);
            } catch (error) {
                console.log('Error fetching slot:', error);
            }
        };

        const intervalId = setInterval(() => {
            fetchSlot();
        }, LiquidityV3ScannerInterval);

        return () => {
            clearInterval(intervalId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!deployedToken) return;
        if (dex !== availableDex[1]) return;
        receivePoolKeys();
    }, [deployedToken]);

    const injectPoolKeys = async (poolKeys: LiquidityPoolKeysV4) => {
        setPoolKeys(poolKeys);
        setTokenPoolKeys(deployedToken, poolKeys);
    };

    async function receivePoolKeys(): Promise<LiquidityPoolKeysV4> {
        let keys: LiquidityPoolKeysV4 | undefined = poolKeys;
        if (keys) return keys;

        let messageDisplayed = false;
        while (!keys) {
            try {
                keys = await fetchPoolKeys(deployedToken);
            } catch {
                if (!messageDisplayed) {
                    logMessage('No AMM pool found, waiting for deployment', 'error');
                    messageDisplayed = true;
                }
            }
            await new Promise((resolve) => setTimeout(resolve, 500));
        }

        injectPoolKeys(keys);

        logMessage(`AMM pool found ${keys.id}`, 'success');
        return keys;
    }

    useEffect(() => {
        let currentMint: string;
        const fetchData = async () => {
            try {
                const mintInfo = await getCurrentMint(Number(sessionId));
                const mint = mintInfo.mint;

                const updatedAt = mintInfo.updatedAt;
                setLastUpdatedTokenTime(updatedAt);

                const address = JSON.stringify(mint).substring(1, mint.length + 1);
                if (currentMint !== address) {
                    if (currentMint && autoRefresh === 'true') {
                        console.log('Reloading page');
                        window.location.reload();
                    }
                    currentMint = address;
                    await injectToken(address);
                }
            } catch (error) {
                console.log('Error getting current mint:', error);
            }
        };
        let intervalId: NodeJS.Timeout;
        if (role.toUpperCase() === 'SLAVE') {
            intervalId = setInterval(() => {
                fetchData();
            }, 3000);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role]);

    return (
        <div className='container'>
            <Navbar dex={dex} />
            <div className='main'>
                <LoadWallet
                    connection={mainConnection}
                    wallet={wallet}
                    setWallet={setWallet}
                    walletLoaded={walletLoaded}
                    setWalletLoaded={setWalletLoaded}
                    setFunder={setFunder}
                    setVolFunder={setVolFunder}
                    setBundleBuyKeypairs={setBundleBuyKeypairs}
                    setBundleBuyValues={setBundleBuyValues}
                    setHolderKeypairs={setHolderKeypairs}
                    setMixKeypairs={setMixKeypairs}
                    setFreezeKeypairs={setFreezers}
                    setAllUniqueWallets={setAllUniqueWallets}
                    setAllWallets={setAllWallets}
                    setAllWalletRoles={setAllWalletRoles}
                    setInitialTotalSolBalance={setInitialTotalSolBalance}
                    deployedToken={deployedToken}
                    setDeployedToken={setDeployedToken}
                    csvFile={csvFile}
                    setCsvFile={setCsvFile}
                    setParsedCsvData={setParsedCsvData}
                    setVolumeBuyWallets={setVolumeBuyWallets}
                    setVolumeBuyValues={setVolumeBuyValues}
                    deployedTokenName={deployedTokenName}
                    setDeployedTokenName={setDeployedTokenName}
                    deployedTokenSymbol={deployedTokenSymbol}
                    setDeployedTokenSymbol={setDeployedTokenSymbol}
                    csvUrlParam={csv}
                    poolBalance={poolBalance}
                    logMessage={logMessage}
                    totalWalletsBalance={totalWalletsBalance}
                    setTotalWalletsBalance={setTotalWalletsBalance}
                    deployer={wallet}
                    allWallets={allWallets}
                    volumeBuyKeypairs={volumeBuyWallets}
                    setDeployedMarketId={setDeployedMarketId}
                    setPoolKeys={setPoolKeys}
                    injectToken={injectToken}
                    currentSlot={currentSlot}
                >
                    <div className='node-select-wrap'>
                        <label>
                            Node Scan:
                            <select value={mainConnectionKey} onChange={mainRpcChange}>
                                {Object.keys(rpcOptions).map((key) => (
                                    <option value={key} key={key}>
                                        {key}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <div className='dex-select-wrap'>
                        <label>
                            Dex:
                            <select
                                value={dex}
                                onChange={(e) => {
                                    setDex(e.target.value as Dex);
                                }}
                            >
                                {availableDex.map((dex) => (
                                    <option value={dex} key={dex}>
                                        {dex}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                </LoadWallet>
                <Footer></Footer>
                <Routes>
                    <Route
                        path='/'
                        element={
                            <Home
                                //all connections
                                connections={Object.values(rpcOptions)}
                                connection={mainConnection}
                                csvFile={csvFile}
                                allWallets={allWallets}
                                allWalletRoles={allWalletRoles}
                                bundleBuyValues={bundleBuyValues}
                                deployedToken={deployedToken}
                                logMessage={logMessage}
                            />
                        }
                    />
                    <Route
                        path='TokenManagerCsv'
                        element={
                            <TokenManagerCsv
                                connection={mainConnection}
                                deployer={wallet}
                                deployedTokenMint={deployedToken}
                                funder={funder}
                                freezer={freezers[0]}
                                allUniqueWallets={[...allUniqueWallets, ...volumeBuyWallets]}
                                volumeBuyWallets={volumeBuyWallets}
                                allWallets={allWallets}
                                allWalletRoles={allWalletRoles}
                                logMessage={logMessage}
                            />
                        }
                    />
                    <Route
                        path='SolManagerCsv'
                        element={
                            <SolManagerCsv
                                connection={mainConnection}
                                allWallets={allWallets}
                                deployer={wallet}
                                walletLoaded={walletLoaded}
                                allWalletRoles={allWalletRoles}
                                mixWallets={mixKeypairs.map((keypair) => keypair.publicKey)}
                                bundleBuyWallets={bundleBuyKeypairs.map((keypair) => keypair.publicKey)}
                                bundleBuyValues={bundleBuyValues}
                                funder={funder}
                                volFunder={volFunder}
                                holder={holderKeypairs[0]}
                                freezer={freezers[0]}
                                volumeBuyWallets={volumeBuyWallets}
                                volumeBuyAmounts={volumeBuyValues}
                                logMessage={logMessage}
                            />
                        }
                    />
                    <Route
                        path='RemoveLiquidity'
                        element={
                            <RemoveLiquidity
                                wallet={wallet}
                                mainConnection={mainConnection}
                                connections={Object.values(rpcOptions)}
                                walletLoaded={walletLoaded}
                                poolBalance={poolBalance}
                                setPoolBalance={setPoolBalance}
                                totalWalletsBalance={totalWalletsBalance}
                                logMessage={logMessage}
                                injectToken={injectToken}
                                deployedToken={deployedToken}
                                updatedAt={lastUpdatedTokenTime}
                                autoAction={autoAction}
                                volFunder={volFunder}
                                // allWallets={allWallets.concat(volumeBuyWallets)}
                            />
                        }
                    />
                    <Route
                        path='CreateTokenForm'
                        element={
                            <CreateTokenForm
                                wallet={wallet}
                                connection={mainConnection}
                                walletLoaded={walletLoaded}
                                deployedToken={deployedToken}
                                holder={holderKeypairs[0]}
                                freezer={freezers[0]}
                                bundleBuyWallets={bundleBuyKeypairs}
                                setDeployedToken={setDeployedToken}
                                setDeployedTokenName={setDeployedTokenName}
                                setDeployedTokenSymbol={setDeployedTokenSymbol}
                                logMessage={logMessage}
                            />
                        }
                    />
                    <Route
                        path='DeployPool'
                        element={
                            <DeployPool
                                wallet={wallet}
                                connection={mainConnection}
                                buyers={bundleBuyKeypairs}
                                bundleBuyValues={bundleBuyValues}
                                deployedMarketId={deployedMarketId}
                                logMessage={logMessage}
                                injectPoolKeys={injectPoolKeys}
                            />
                        }
                    />
                    <Route
                        path='CreateMarket'
                        element={
                            <CreateMarket
                                wallet={wallet}
                                connection={mainConnection}
                                walletLoaded={walletLoaded}
                                deployedToken={deployedToken}
                                setDeployedMarketId={setDeployedMarketId}
                                logMessage={logMessage}
                            />
                        }
                    />

                    <Route
                        path='TokenMixerSales'
                        element={
                            <TokenMixerSales
                                connections={Object.values(rpcOptions)}
                                connection={mainConnection}
                                funder={funder}
                                bundleBuyKeypairs={bundleBuyKeypairs}
                                holderKeypair={holderKeypairs[0]}
                                deployerKeypair={wallet}
                                mixKeypairs={mixKeypairs}
                                deployedToken={deployedToken}
                                allWallets={allWallets}
                                allWalletRoles={allWalletRoles}
                                parsedCsvData={parsedCsvData}
                                initialTotalSolBalance={initialTotalSolBalance}
                                logMessage={logMessage}
                                volumeBuyWallets={volumeBuyWallets}
                                receivePoolKeys={receivePoolKeys}
                                updatedAt={lastUpdatedTokenTime}
                            />
                        }
                    />
                    <Route
                        path='ManualSwap'
                        element={<ManualSwap connection={mainConnection} allWallets={allWallets} allWalletRoles={allWalletRoles} logMessage={logMessage} />}
                    />
                    <Route
                        path='Freezer'
                        element={
                            <Freezer
                                connection={mainConnection}
                                deployedToken={deployedToken}
                                freezer={freezers[0]}
                                allWallets={allUniqueWallets.map((wallet) => wallet.publicKey.toBase58())}
                                volumeBuyWallets={volumeBuyWallets.map((wallet) => wallet.publicKey.toBase58())}
                                logMessage={logMessage}
                                injectToken={injectToken}
                                updatedAt={lastUpdatedTokenTime}
                                autoAction={autoAction}
                            />
                        }
                    />
                    <Route
                        path='VolumeWalletControl'
                        element={
                            <VolumeWalletControl
                                connection={mainConnection}
                                deployedToken={deployedToken}
                                volumeBuyWallets={removeKeypairDuplicates(volumeBuyWallets)}
                                logMessage={logMessage}
                                injectToken={injectToken}
                                updatedAt={lastUpdatedTokenTime}
                                autoAction={autoAction}
                                receivePoolKeys={receivePoolKeys}
                                poolKeys={poolKeys}
                            />
                        }
                    />

                    <Route
                        path='RapidLaunch'
                        element={
                            <RapidLaunch
                                connection={mainConnection}
                                deployer={wallet}
                                funder={funder}
                                holder={holderKeypairs[0]}
                                freezer={freezers[0]}
                                bundleBuyWallets={bundleBuyKeypairs}
                                bundleBuyValues={bundleBuyValues}
                                volumeBuyWallets={volumeBuyWallets}
                                volumeBuyAmounts={volumeBuyValues}
                                mixWallets={mixKeypairs}
                                logMessage={logMessage}
                                injectToken={injectToken}
                                tokenAddress={deployedToken}
                                setDeployedMarketId={setDeployedMarketId}
                                deployedMarketId={deployedMarketId}
                                mixKeypairs={mixKeypairs}
                                volFunder={volFunder}
                                role={role.toUpperCase()}
                            />
                        }
                    />
                    <Route
                        path='VolumeMakerV2'
                        element={
                            <VolumeMakerV2
                                logMessage={logMessage}
                                connection={mainConnection}
                                volumeBuyWallets={volumeBuyWallets}
                                volFunder={volFunder}
                                deployedToken={deployedToken}
                                autoAction={autoAction}
                                updatedAt={lastUpdatedTokenTime}
                                receivePoolKeys={receivePoolKeys}
                                poolKeys={poolKeys}
                            />
                        }
                    />
                    <Route
                        path='LiquidityV3'
                        element={
                            <LiquidityV3
                                wallet={wallet}
                                mainConnection={mainConnection}
                                connections={Object.values(rpcOptions)}
                                walletLoaded={walletLoaded}
                                poolBalance={poolBalance}
                                setPoolBalance={setPoolBalance}
                                totalWalletsBalance={totalWalletsBalance}
                                logMessage={logMessage}
                                deployedToken={deployedToken}
                                updatedAt={lastUpdatedTokenTime}
                                autoAction={autoAction}
                                allWallets={allWallets.concat(volumeBuyWallets)}
                                poolKeys={poolKeys}
                                receivePoolKeys={receivePoolKeys}
                                currentSlot={currentSlot}
                            />
                        }
                    />
                    <Route
                        path='VolumeMakerPumpswap'
                        element={
                            <VolumeMakerPumpswap
                                tokenDeployer={wallet}
                                connection={mainConnection}
                                deployedToken={deployedToken}
                                volumeBuyWallets={volumeBuyWallets}
                                autoAction={autoAction}
                                updatedAt={lastUpdatedTokenTime}
                                volFunder={volFunder}
                                logMessage={logMessage}
                            />
                        }
                    />
                    <Route
                        path='VolumeSellerPumpswap'
                        element={
                            <VolumeSellerPumpswap
                                tokenDeployer={wallet}
                                volumeBuyWallets={volumeBuyWallets}
                                connection={mainConnection}
                                deployedToken={deployedToken}
                                logMessage={logMessage}
                                updatedAt={lastUpdatedTokenTime}
                                autoAction={autoAction}
                            />
                        }
                    />
                    <Route
                        path='RemoveLiquidityPumpswap'
                        element={
                            <RemoveLiquidityPumpswap
                                wallet={wallet}
                                mainConnection={mainConnection}
                                connections={Object.values(rpcOptions)}
                                walletLoaded={walletLoaded}
                                totalWalletsBalance={totalWalletsBalance}
                                logMessage={logMessage}
                                deployedToken={deployedToken}
                                updatedAt={lastUpdatedTokenTime}
                                autoAction={autoAction}
                                allWallets={allWallets.concat(volumeBuyWallets)}
                                currentSlot={currentSlot}
                                volFunder={volFunder}
                            />
                        }
                    />
                    <Route
                        path='DeployPoolPumpswap'
                        element={
                            <DeployPoolPumpswap
                                connection={mainConnection}
                                wallet={wallet}
                                deployedToken={deployedToken}
                                buyers={bundleBuyKeypairs}
                                bundleBuyValues={bundleBuyValues}
                                logMessage={logMessage}
                            />
                        }
                    />
                    <Route
                        path='RapidLaunchPumpswap'
                        element={
                            <RapidLaunchPumpswap
                                mainConnection={mainConnection}
                                connections={Object.values(rpcOptions)}
                                totalWalletsBalance={totalWalletsBalance}
                                walletLoaded={walletLoaded}
                                logMessage={logMessage}
                                deployedToken={deployedToken}
                                wallet={wallet}
                                bundleBuyWallets={bundleBuyKeypairs}
                                funder={funder}
                                holder={holderKeypairs[0]}
                                freezer={freezers[0]}
                                volFunder={volFunder}
                                volumeBuyWallets={volumeBuyWallets}
                                mixWallets={mixKeypairs}
                                bundleBuyValues={bundleBuyValues}
                                volumeBuyAmounts={volumeBuyValues}
                                allWallets={allWallets.concat(volumeBuyWallets)}
                                setDeployedToken={setDeployedToken}
                                setDeployedTokenName={setDeployedTokenName}
                                setDeployedTokenSymbol={setDeployedTokenSymbol}
                                currentSlot={currentSlot}
                            />
                        }
                    />
                </Routes>
            </div>
            {isSidebarExpanded && (
                <div className='logger'>
                    <Logger messages={messages} />
                </div>
            )}
            <div className='logger-button-container'>
                <button
                    className={`toggle-sidebar-button ${isSidebarExpanded ? 'collapse-icon' : 'expand-icon'}`}
                    onClick={() => setSidebarExpanded(!isSidebarExpanded)}
                ></button>
            </div>
            {isSidebarExpanded && (
                <div className='logger-button-container'>
                    <button className='clear-logger-button clear-icon' onClick={() => setMessages([])}></button>
                </div>
            )}
        </div>
    );
}

export default App;
